import Tabs from "./tabs";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatDateWithTime } from "../../../components/formatDateAndTime/formatDateAndTime";
import { Box, Stack, Typography, Avatar, Tooltip } from "@mui/material";
import { FieldSelect } from "../../../components/selections/fieldInputs";
import CampaignIcon from '@mui/icons-material/Campaign';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import FilterListIcon from '@mui/icons-material/FilterList';
import { QR, QrActionButton } from "../../qrCode/components/qrUi";
import RefreshIcon from '@mui/icons-material/Refresh';
import { getCampaignsThunk } from "../campaignThunk";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import { APP_ROUTES } from "../../../config/appRoutes";
import { useNavigate } from "react-router-dom";
import { setViewCampaign } from "../campaignSlice";
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import { formatDateWithTimeWithoutSec } from "../../../components/formatDateAndTime/formatDateAndTime";

// Count pill (display only)
const CountPill = ({ value, color }) =>
    Number(value) > 0 ? (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 32,
                px: 1,
                py: 0.25,
                borderRadius: 5,
                fontSize: 12,
                fontWeight: 700,
                color,
                bgcolor: `${color}14`,
                border: `1px solid ${color}33`,
            }}
        >
            {new Intl.NumberFormat("en-IN").format(Number(value))}
        </Box>
    ) : (
        <Typography variant="body2" color="text.disabled">0</Typography>
    );

export default function CampaignList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { getcampaigns, status, error } = useSelector((state) => state.campaigns);

    const [state, setState] = useState({
        selectStatus: "all",
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // ✅ Build status options dynamically from API data
    const statusOptions = useMemo(() => {
        if (!getcampaigns || getcampaigns.length === 0) return [{ value: "all", label: "All" }];

        const uniqueStatuses = Array.from(
            new Set(
                getcampaigns
                    .map((item) => item.workflow_status)
                    .filter((status) => status && status.trim() !== "")
            )
        );

        const formattedOptions = uniqueStatuses.map((status) => ({
            value: status.toLowerCase(),
            label: status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " "),
        }));

        return [{ value: "all", label: "All" }, ...formattedOptions];
    }, [getcampaigns]);

    const columns = useMemo(() => [
        { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
        {
            accessorKey: "campaign_name",
            header: "Campaign Name",
            Cell: ({ row }) => {
                const campaignName = row.original.campaign_name || "";
                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                        title="View campaign"
                        sx={{
                            cursor: "pointer",
                            // clickable: always blue + underlined (darker on hover)
                        "& .cmp-name": { color: "#2563eb !important", textDecoration: "underline", textDecorationColor: "#2563eb66", textUnderlineOffset: "3px" },
                        "&:hover .cmp-name": { color: "#1d4ed8 !important", textDecorationColor: "#1d4ed8" },
                            "&:hover .cmp-icon": { bgcolor: QR.blue, color: "#fff" },
                        }}
                        onClick={() => {
                            navigate(APP_ROUTES.CAMPAIGN.VIEW_CAMPAIGN);
                            dispatch(setViewCampaign([row.original]));
                        }}
                    >
                        <Avatar
                            className="cmp-icon"
                            variant="rounded"
                            sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: `${QR.main}14`, color: QR.main, transition: "all .15s ease" }}
                        >
                            <CampaignIcon sx={{ fontSize: 17 }} />
                        </Avatar>
                        <Typography className="cmp-name" variant="body2" fontWeight={700} color="#0f172a" noWrap>
                            {campaignName}
                        </Typography>
                    </Stack>
                );
            },
        },
        {
            accessorKey: "workflow_status",
            header: "Status",
            Cell: ({ row }) => {
                const status = row.original.workflow_status || "";

                const colorMap = {
                    completed: "#16a34a",
                    created: "#d97706",
                    failed: "#dc2626",
                    pending: "#0284c7",
                    scheduled: "#7c3aed",
                    cancelled: "#dc2626",
                    in_process: "#2563eb",
                };

                const c = colorMap[status.toLowerCase()] || "#475569";

                // Soft status chip: dot + label
                return (
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.6,
                            px: 1,
                            py: 0.3,
                            borderRadius: 5,
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: "capitalize",
                            whiteSpace: "nowrap",
                            color: c,
                            bgcolor: `${c}14`,
                            border: `1px solid ${c}33`,
                        }}
                    >
                        <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c }} />
                        {status.replaceAll("_", " ")}
                    </Box>
                );
            },
        },
        {
            accessorKey: "notification_type",
            header: "Notification Type",
            Cell: ({ row }) =>
                row.original.notification_type ? (
                    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1.5, fontSize: 12, fontWeight: 600, color: "#334155", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0", textTransform: "capitalize" }}>
                        {row.original.notification_type}
                    </Box>
                ) : "-",
        },
        { accessorKey: "Attempted", header: "Attempted", Cell: ({ row }) => <CountPill value={row.original.Attempted} color="#475569" /> },
        { accessorKey: "Sent", header: "Sent", Cell: ({ row }) => <CountPill value={row.original.Sent} color={QR.blue} /> },
        { accessorKey: "Delivered", header: "Delivered", Cell: ({ row }) => <CountPill value={row.original.Delivered} color={QR.main} /> },
        { accessorKey: "Read", header: "Read", Cell: ({ row }) => <CountPill value={row.original.Read} color={QR.purple} /> },
        { accessorKey: "Failed", header: "Failed", Cell: ({ row }) => <CountPill value={row.original.Failed} color="#dc2626" /> },
        {
            accessorKey: "scheduled_time",
            header: "Scheduled Time",
            Cell: ({ row }) => (
                <Typography variant="body2" color="text.secondary">
                    {row.original.scheduled_time ? formatDateWithTimeWithoutSec(row.original.scheduled_time) : "-"}
                </Typography>
            ),
        },
    ], [navigate, dispatch]);

    const handleSingleSelectChange = (name, selectedValue) => {
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    // ✅ Status filter dropdown renderer
    // Reload the campaign list (same as the old Sync: refetch campaigns)
    const syncCampaigns = () => {
        dispatch(getCampaignsThunk({ type: "getCampaigns" }));
    };

    const renderCampaignFilter = () => (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
        <Box sx={{ width: { xs: "100%", sm: 240 } }}>
            <FieldSelect
                name="selectStatus"
                label="Status"
                options={statusOptions}
                value={state.selectStatus}
                onChange={handleSingleSelectChange}
                icon={<FilterListIcon />}
                placeholder="All"
            />
        </Box>
        {/* Reload: right side of the toolbar */}
        <Tooltip title="Fetch the latest campaigns and delivery counts" placement="top" arrow>
            <Box sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
                <QrActionButton
                    icon={<RefreshIcon sx={{ fontSize: 18 }} />}
                    label="Reload"
                    loading={status === "loading"}
                    loadingLabel="Reloading..."
                    onClick={syncCampaigns}
                />
            </Box>
        </Tooltip>
        </Box>
    );

    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);

    // ✅ Apply dynamic status filter
    const filteredCampaigns =
        state.selectStatus === "all"
            ? getcampaigns
            : getcampaigns.filter(
                (item) =>
                    item.workflow_status &&
                    item.workflow_status.toLowerCase() === state.selectStatus.toLowerCase()
            );

    // Summary cards (display only, from the loaded campaigns)
    const all = getcampaigns || [];
    const sum = (key) => all.reduce((t, c) => t + (Number(c[key]) || 0), 0);
    const countStatus = (st) => all.filter((c) => String(c.workflow_status || "").toLowerCase() === st).length;
    const delivered = sum("Delivered");
    const readRate = delivered ? Math.round((sum("Read") / delivered) * 100) : 0;
    const fmt = (n) => new Intl.NumberFormat("en-IN").format(n);
    const stats = [
        { label: "Campaigns", value: fmt(all.length), icon: <CampaignIcon />, color: QR.main },
        { label: "Completed", value: fmt(countStatus("completed")), icon: <TaskAltIcon />, color: "#16a34a" },
        { label: "Scheduled", value: fmt(countStatus("scheduled")), icon: <ScheduleIcon />, color: QR.purple },
        { label: "Messages Sent", value: fmt(sum("Sent")), icon: <SendIcon />, color: QR.blue },
        { label: "Read Rate", value: `${readRate}%`, icon: <DoneAllIcon />, color: QR.amber },
    ];

    return (
        <div>
            <Tabs stats={stats}>
                <DataTableWithCheckbox
                    columns={columns}
                    data={filteredCampaigns || []}
                    renderFilters={renderCampaignFilter()}
                    status={status}
                    enableRowSelection={false}
                    searchFirst
                    emptyMessage={state.selectStatus === "all" ? "No campaigns yet. Create one to get started." : "No campaigns with this status."}
                />
            </Tabs>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </div>
    );
}
